import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiLinkedin, FiYoutube, FiTwitter, FiMail, FiMapPin, FiPhone, FiUsers } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Footer.css';

const Footer = () => {
  const { t, i18n } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/contact/newsletter', { email });
      setSubscribed(true);
      setEmail('');
      setError('');
    } catch (err) {
      setError('Failed to subscribe. Please try again.');
    }
  };

  /**
   * 页脚法务行 —— 与 Precision 站点同一做法。
   *
   * 为什么不只留 /privacy + /terms：用户在结账、注册和上传资料时受约束的，
   * 是私隐政策与用户条款之外的那些文件（退款规则、AI 政策、可接受使用、跨境告知）。
   * 只链两条，等于把其余十二份公布在一个没有入口的地址上。
   * 这里挑最常被引用的几份，其余经 /legal 索引进入。
   *
   * 标签直接写死中英对照而非走翻译字典：条款名称必须与 POLICIES/ 里的标题一致，
   * 让字典去改它，就等于让一次词条改动悄悄改掉一份文件的引用名。
   */
  const zh = String(i18n.language).toLowerCase().startsWith('zh');
  const FOOTER_LEGAL = [
    { to: '/privacy',                          label: zh ? '私隐政策' : 'Privacy' },
    { to: '/terms',                            label: zh ? '用户条款' : 'Terms of Use' },
    { to: '/legal/06-order-payment-and-refund', label: zh ? '订单与退款' : 'Payment and Refunds' },
    { to: '/legal/07-ai-features-policy',       label: zh ? 'AI 功能政策' : 'AI Features' },
    { to: '/legal/08-acceptable-use-policy',    label: zh ? '可接受使用' : 'Acceptable Use' },
    { to: '/legal/10-cross-border-data-notice', label: zh ? '跨境资料' : 'Cross-Border Data' },
    { to: '/legal/12-accessibility-statement',  label: zh ? '无障碍' : 'Accessibility' },
    { to: '/legal',                            label: zh ? '全部法律文书' : 'All legal' },
  ];

  const footerLinks = {
    solutions: [
      { label: t('footer.links.solutions.marketIntelligence'), path: '/services#intelligence' },
      { label: t('footer.links.solutions.digitalCommerce'), path: '/services#commerce' },
      { label: t('footer.links.solutions.technology'), path: '/services#technology' },
      { label: t('footer.links.solutions.operations'), path: '/services#operations' }
    ],
    company: [
      { label: t('footer.links.company.aboutUs'), path: '/about' },
      { label: t('footer.links.company.ourTeam'), path: '/about#team' },
      { label: t('footer.links.company.careers'), path: '/careers' },
      { label: t('footer.links.company.contact'), path: '/contact' }
    ],
    insights: [
      { label: t('footer.links.insights.blog'), path: '/insights?type=blog' },
      { label: t('footer.links.insights.caseStudies'), path: '/case-studies' },
      { label: t('footer.links.insights.reports'), path: '/insights?type=report' },
      { label: t('footer.links.insights.news'), path: '/insights?type=news' }
    ]
  };

  const offices = t('footer.offices', { returnObjects: true });

  return (
    <footer className="footer">
      {/* CTA Section */}
      <div className="footer-cta">
        <div className="container">
          <div className="cta-content">
            <h2>{t('footer.cta.title')}</h2>
            <p>{t('footer.cta.description')}</p>
          </div>
          <Link to="/contact" className="btn btn-primary">
            {t('footer.cta.button')}
          </Link>
        </div>
      </div>

      {/* Main Footer */}
      <div className="footer-main">
        <div className="container">
          <div className="footer-grid">
            {/* Brand Column */}
            <div className="footer-brand">
              <Link to="/" className="footer-logo">
                <img src="/images/logo-mark.svg" alt={t('company.fullName')} className="logo-image" />
                <div className="logo-text-container">
                  <span className="logo-company-name">{t('company.name')}</span>
                  <span className="logo-company-type">{t('company.type')}</span>
                </div>
              </Link>
              <p className="footer-description">
                {t('footer.description')}
              </p>
              
              {/* Newsletter */}
              <div className="newsletter">
                <h4>{t('footer.newsletterTitle')}</h4>
                {subscribed ? (
                  <p className="success-message">{t('footer.thankYou')}</p>
                ) : (
                  <form onSubmit={handleSubscribe} className="newsletter-form">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                    <button type="submit" className="btn btn-primary">
                      {t('footer.subscribe')}
                    </button>
                  </form>
                )}
                {error && <p className="error-message">{error}</p>}
              </div>

              {/* Social Links */}
              <div className="social-links">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <FiLinkedin />
                </a>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                  <FiYoutube />
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                  <FiTwitter />
                </a>
              </div>
            </div>

            {/* Solutions Links */}
            <div className="footer-links">
              <h4>{t('footer.sections.solutions')}</h4>
              <ul>
                {footerLinks.solutions.map((link, index) => (
                  <li key={index}>
                    <Link to={link.path}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company Links */}
            <div className="footer-links">
              <h4>{t('footer.sections.company')}</h4>
              <ul>
                {footerLinks.company.map((link, index) => (
                  <li key={index}>
                    <Link to={link.path}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Insights Links */}
            <div className="footer-links">
              <h4>{t('footer.sections.insights')}</h4>
              <ul>
                {footerLinks.insights.map((link, index) => (
                  <li key={index}>
                    <Link to={link.path}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div className="footer-contact">
              <h4>{t('footer.contactUs')}</h4>
              <ul className="contact-list">
                <li>
                  <FiMail />
                  <a href="mailto:contact@infini-nova.com">contact@infini-nova.com</a>
                </li>
                <li>
                  {/* 合作伙伴专线：与一般咨询分开，避免商务合作淹没在客服邮件里 */}
                  <FiUsers />
                  <a href="mailto:partnerservice@infini-nova.com">
                    <span className="contact-label">{t('footer.partnerService')}</span>
                    partnerservice@infini-nova.com
                  </a>
                </li>
                <li>
                  <FiPhone />
                  <a href="tel:+15025369198">{t('contact.phoneNumber')}</a>
                </li>
              </ul>
              
              <h4 className="offices-title">{t('footer.globalOffices')}</h4>
              <div className="offices-list">
                {offices.map((office, index) => (
                  <span key={index} className="office-tag">
                    <FiMapPin /> {office}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="footer-bottom">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} {t('company.fullName')}. {t('footer.copyright')}</p>
          <div className="footer-legal">
            {FOOTER_LEGAL.map((link) => (
              <Link key={link.to} to={link.to}>{link.label}</Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
